import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { customerService, Customer, CustomerResponse } from '../services/customerService';
import { Users, Search, Phone, Calendar, Trash2, Mail, Building, Award } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import Pagination from '../components/common/Pagination';

export default function CustomersPage() {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState<number>(() => {
    const saved = localStorage.getItem('customersPageLimit');
    return saved ? parseInt(saved, 10) : 20;
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; customer: Customer | null }>({ show: false, customer: null });
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm, limit]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerService.getCustomers({
        page: currentPage,
        limit,
        search: searchTerm || undefined,
        sortBy: 'created_at',
        sortOrder: 'desc'
      });

      if (Array.isArray(response)) {
        setCustomers(response);
        setTotal(response.length);
        setTotalPages(1);
      } else {
        const data = response as CustomerResponse;
        setCustomers(data.customers);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1);
    localStorage.setItem('customersPageLimit', newLimit.toString());
  };

  const handleCustomerClick = (customerId: number, e?: React.MouseEvent) => {
    // Don't navigate if clicking on the delete button
    if (e && (e.target as HTMLElement).closest('button')) {
      return;
    }
    navigate(`/customers/${customerId}`);
  };

  const handleDeleteClick = (customer: Customer, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmation({ show: true, customer });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.customer) return;

    setDeleting(true);
    try {
      await customerService.deleteCustomer(deleteConfirmation.customer.id);
      showToast('Customer deleted successfully', 'success');
      setDeleteConfirmation({ show: false, customer: null });
      // Refresh the customer list
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to delete customer',
        'error'
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ show: false, customer: null });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <Container className={`py-6 space-y-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        {/* Page Header */}
        <div className="flex flex-col gap-2">
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
            Customer Management
          </h1>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            View and manage customer information and ticket history
          </p>
        </div>

        {/* Search Bar */}
        <div className={`p-4 rounded-lg shadow-sm border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          <div className="flex items-center space-x-3">
            <Search className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
            <input
              type="text"
              placeholder="Search by name, phone, or external reference..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className={`flex-1 bg-transparent border-none focus:outline-none focus:ring-0 ${theme === 'dark' ? 'text-gray-100 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                }`}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className={`relative overflow-hidden rounded-lg p-4 shadow-sm border transition-all duration-300 hover:translate-y-[-2px] ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                  {total}
                </p>
                <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total Customers
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
                }`}>
                <Users className={`h-5 w-5 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`} />
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-1 w-full ${theme === 'dark' ? 'bg-blue-500/40' : 'bg-blue-500/30'}`}></div>
          </div>
        </div>

        {/* Customer List */}
        <div className={`rounded-lg shadow-sm border overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading customers...</p>
            </div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className={`h-12 w-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {searchTerm ? 'No customers found matching your search' : 'No customers yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Customer
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Contact
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Company / Type
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Stage
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        Created
                      </th>
                      {isAdmin && (
                        <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {customers.map((customer: any) => (
                      <tr
                        key={customer.id}
                        onClick={(e) => handleCustomerClick(customer.id, e)}
                        className={`cursor-pointer transition-colors ${theme === 'dark'
                          ? 'hover:bg-gray-700'
                          : 'hover:bg-gray-50'
                          }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                              customer.is_vip
                                ? theme === 'dark' ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-600'
                                : theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'
                              }`}>
                              {customer.full_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="ml-4">
                              <div className={`text-sm font-medium flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                                }`}>
                                {customer.full_name}
                                {customer.is_vip && (
                                  <Award className={`ml-1 h-4 w-4 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`} />
                                )}
                              </div>
                              {customer.external_ref && (
                                <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Ref: {customer.external_ref}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <Phone className={`h-3.5 w-3.5 mr-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                              <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                                {customer.phone}
                              </span>
                            </div>
                            {customer.email && (
                              <div className="flex items-center">
                                <Mail className={`h-3.5 w-3.5 mr-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {customer.email}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {customer.company_name && (
                              <div className="flex items-center">
                                <Building className={`h-3.5 w-3.5 mr-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                                  {customer.company_name}
                                </span>
                              </div>
                            )}
                            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                              customer.customer_type === 'business'
                                ? theme === 'dark' ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                                : theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                            }`}>
                              {customer.customer_type === 'business' ? 'Business' : 'Individual'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            customer.lifecycle_stage === 'customer'
                              ? theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                              : customer.lifecycle_stage === 'loyal'
                              ? theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                              : customer.lifecycle_stage === 'at_risk'
                              ? theme === 'dark' ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                              : customer.lifecycle_stage === 'churned'
                              ? theme === 'dark' ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
                              : customer.lifecycle_stage === 'prospect'
                              ? theme === 'dark' ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                              : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.lifecycle_stage ? customer.lifecycle_stage.charAt(0).toUpperCase() + customer.lifecycle_stage.slice(1) : 'Lead'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className={`h-4 w-4 mr-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatDate(customer.created_at)}
                            </span>
                          </div>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={(e) => handleDeleteClick(customer, e)}
                              className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                                  ? 'hover:bg-red-900/20 text-red-400 hover:text-red-300'
                                  : 'hover:bg-red-50 text-red-600 hover:text-red-700'
                                }`}
                              title="Delete customer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`px-6 py-4 flex items-center justify-center border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    itemsPerPageOptions={[10, 20, 50, 100]}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmation.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg shadow-xl max-w-md w-full mx-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              }`}>
              <div className="p-6">
                <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                  Confirm Delete
                </h3>
                <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                  Are you sure you want to delete customer <strong>{deleteConfirmation.customer?.full_name}</strong>? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={deleting}
                    className={`px-4 py-2 rounded-lg transition-colors ${theme === 'dark'
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </DashboardLayout>
  );
}
